import { Arrange, Calendar, EyeShowVisible, TodoList2 } from '@griply/assets';
import {
  CalendarDetailsVisibility,
  CalendarRef,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogVariantOptions } from 'shared/assets/styles';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { CalendarSelect } from 'shared/components/ui/calendar-select';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Icon } from 'shared/components/ui/icon';
import { ConnectionCalendar } from 'shared/hooks/queries/use-external-connections-calendars-queries';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';

import { getCalendarSyncFieldOptions } from './calendar-sync-field-options';
import * as Styled from './calendar-sync-form-dialog.style';
import { CalendarSyncLinkedItemSelect } from './calendar-sync-linked-item-select';
import { SettingsInfoIcon } from './settings-info-icon';

export type CalendarSyncFormValue = {
  linkedItemId: ID;
  linkedItemType: LinkedItemType;
  calendar: CalendarRef;
  mode: SyncMode;
  detailsVisibility: CalendarDetailsVisibility;
};

export type CalendarSyncFormDialogProps = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  isLoadingCalendars: boolean;
  onCancel: () => void;
  onSave: (value: CalendarSyncFormValue) => void;
};

// Every field is settled before anything is written: picking a target and a calendar used to
// create the sync on the spot, which left no moment to set the mode or hide the details first.
// Mount this conditionally — the draft below is per-open state.
export const CalendarSyncFormDialog: React.FC<CalendarSyncFormDialogProps> = ({
  goals,
  lifeAreas,
  calendars,
  selectableCalendars,
  isLoadingCalendars,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();

  const [linkedItem, setLinkedItem] = useState<{
    id: ID;
    type: LinkedItemType;
  } | null>(null);
  const [calendar, setCalendar] = useState<CalendarRef | null>(null);
  const [mode, setMode] = useState(SyncMode.Automatic);
  const [detailsVisibility, setDetailsVisibility] = useState(
    CalendarDetailsVisibility.Visible,
  );

  const { modeOptions, visibilityOptions } = getCalendarSyncFieldOptions(t);

  const onSelectMode = (value?: string) => {
    if (!value) {
      return;
    }

    setMode(value as SyncMode);
  };

  const onSelectVisibility = (value?: string) => {
    if (!value) {
      return;
    }

    setDetailsVisibility(value as CalendarDetailsVisibility);
  };

  const canSave = !!linkedItem && !!calendar;

  const onSubmit = () => {
    if (!linkedItem || !calendar) {
      return;
    }

    onSave({
      linkedItemId: linkedItem.id,
      linkedItemType: linkedItem.type,
      calendar,
      mode,
      detailsVisibility,
    });
  };

  return (
    <Styled.Dialog open onClose={onCancel} variant={DialogVariantOptions.Small}>
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {t('calendar.sync-tasks.add')}
        </Typography>
        <Typography variant="body2" component="h1">
          {t('calendar.sync-tasks.intro')}
        </Typography>
      </Styled.Header>

      <Styled.Body>
        <Styled.Item>
          <Styled.LabelCell>
            <Styled.Label>
              <Icon icon={TodoList2} />
              <Styled.LabelText>
                {t('calendar.sync-tasks.fields.tasks')}
              </Styled.LabelText>
            </Styled.Label>
            <SettingsInfoIcon label={t('calendar.sync-tasks.info.tasks')} />
          </Styled.LabelCell>
          <CalendarSyncLinkedItemSelect
            linkedItemId={linkedItem?.id ?? null}
            goals={goals}
            lifeAreas={lifeAreas}
            disabled={isLoadingCalendars}
            onChange={setLinkedItem}
          />
        </Styled.Item>

        <Styled.Item>
          <Styled.LabelCell>
            <Styled.Label>
              <Icon icon={Calendar} />
              <Styled.LabelText>
                {t('calendar.sync-tasks.fields.calendar')}
              </Styled.LabelText>
            </Styled.Label>
            <SettingsInfoIcon label={t('calendar.sync-tasks.info.calendar')} />
          </Styled.LabelCell>
          <CalendarSelect
            calendars={calendars}
            selectableCalendars={selectableCalendars}
            value={calendar}
            isLoading={isLoadingCalendars}
            onChange={setCalendar}
          />
        </Styled.Item>

        <Styled.Item>
          <Styled.LabelCell>
            <Styled.Label>
              <Icon icon={Arrange} />
              <Styled.LabelText>
                {t('calendar.sync-tasks.fields.task-sync')}
              </Styled.LabelText>
            </Styled.Label>
            <SettingsInfoIcon label={t('calendar.sync-tasks.info.task-sync')} />
          </Styled.LabelCell>
          <CustomFormSelect
            placeholder={t('calendar.sync-tasks.placeholder')}
            options={modeOptions}
            value={mode}
            onChange={onSelectMode}
            size={SelectSize.ExtraSmall}
            noOverflow
            disabled={isLoadingCalendars}
          />
        </Styled.Item>

        <Styled.Item>
          <Styled.LabelCell>
            <Styled.Label>
              <Icon icon={EyeShowVisible} />
              <Styled.LabelText>
                {t('calendar.sync-tasks.fields.task-details')}
              </Styled.LabelText>
            </Styled.Label>
            <SettingsInfoIcon
              label={t('calendar.sync-tasks.info.task-details')}
            />
          </Styled.LabelCell>
          <CustomFormSelect
            placeholder={t('calendar.sync-tasks.placeholder')}
            options={visibilityOptions}
            value={detailsVisibility}
            onChange={onSelectVisibility}
            size={SelectSize.ExtraSmall}
            noOverflow
            disabled={isLoadingCalendars}
          />
        </Styled.Item>
      </Styled.Body>

      <Styled.Footer>
        <Button
          type="button"
          variant={Variants.Contained}
          size={Sizes.Medium}
          color={Colors.White}
          onClick={onCancel}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="button"
          size={Sizes.Medium}
          onClick={onSubmit}
          disabled={!canSave || isLoadingCalendars}
        >
          {t('general.save')}
        </Button>
      </Styled.Footer>
    </Styled.Dialog>
  );
};
